import React, { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useWaitlistProcessor } from "@/hooks/use-waitlist-processor";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({ 
  user: null, 
  session: null,
  isLoading: true
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // We're not using sendWelcomeEmail in this component anymore
  // because we're delegating that responsibility to the registration flow
  const { processStoredWaitlistData } = useWaitlistProcessor();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log("Auth state changed:", event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        
        if (event === 'SIGNED_IN' && session) {
          // We're removing the welcome email sending from here
          // since it should only be sent during registration
          
          // Set flag for waitlist flow
          if (!sessionStorage.getItem('justCompletedAuth')) {
            sessionStorage.setItem('justCompletedAuth', 'true');
          }
          
          toast.success(`Welcome${session.user.user_metadata?.first_name ? ' ' + session.user.user_metadata.first_name : ''}!`);
          
          // Process waitlist data if needed
          await processStoredWaitlistData(session);
        }
        
        if (event === 'SIGNED_OUT') {
          toast.success("You've been signed out");
          sessionStorage.removeItem('waitlistFormData');
          sessionStorage.removeItem('justCompletedAuth');
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    user,
    session,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
